import { useCallback } from "react";
import PropTypes from "prop-types";
import {
  Content,
  Text,
  IllustratedMessage,
  Heading,
  ButtonGroup,
  LinkButton,
  ActionButton,
} from "@react-spectrum/s2";
import { useNavigate } from "react-router";

import AssetsIllustration from "@react-spectrum/s2/illustrations/gradient/generic2/Assets";
import AddCircle from "@react-spectrum/s2/icons/AddCircle";
import AIGenerateIcon from "icon:@app/assets/AIGenerate.svg";

import { ProjectsList } from "./ProjectsList";
import { getNewVideoPath } from "@app/hooks/videos";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const ProjectsView = ({ isQuiet = false }) => {
  const navigate = useNavigate();

  useBreadcrumbs([{ label: "Projects", href: "/projects" }]);

  const handleCardPress = useCallback(
    ({ id, videoPath, templateId }) => {
      if (!videoPath) {
        navigate(getNewVideoPath(id));
        return;
      }

      navigate(`/projects/${id}`);
    },
    [navigate],
  );

  const renderEmptyState = () => (
    <IllustratedMessage size="L">
      <AssetsIllustration />
      <Heading>Nothing Created So Far</Heading>
      <Content>
        You haven&apos;t made any projects yet—start your first one now!
      </Content>
      <ButtonGroup>
        <LinkButton variant="accent" href="/">
          <AIGenerateIcon />
          <Text>Get started</Text>
        </LinkButton>
      </ButtonGroup>
    </IllustratedMessage>
  );

  return (
    <>
      <title>{`Projects | BeStory`}</title>

      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 6.5rem)",
        })}
      >
        <div
          className={style({
            display: "flex",
            alignItems: "center",
            gap: 12,
          })}
        >
          <Heading styles={style({ font: "title-lg" })}>Projects</Heading>
          <ActionButton
            textValue="New project"
            isQuiet
            onPress={() => navigate("/projects/new")}
          >
            <AddCircle />
          </ActionButton>
        </div>
        <div
          className={style({
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            minHeight: 0,
          })}
        >
          <ProjectsList
            isQuiet={isQuiet}
            renderEmptyState={renderEmptyState}
            onCardPress={handleCardPress}
            styles={style({ size: "full" })}
          />
        </div>
      </div>
    </>
  );
};

ProjectsView.propTypes = {
  isQuiet: PropTypes.bool,
};
